import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getPaymentMethods, createDeposit } from '../../services/paymentService';
import { getKYCDetails } from '../../services/kycService';
import { buildImageUrl } from '../../services/apiConfig';

const DepositNew = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [formData, setFormData] = useState({
        amount: '',
        paymentMethodId: '',
        transactionId: '',
        medium: 'bank_transfer'
    });
    const [limits, setLimits] = useState(null);
    const [fees, setFees] = useState(null);
    const [systemStatus, setSystemStatus] = useState(null);
    const [verificationStatus, setVerificationStatus] = useState(null);
    const [depositResult, setDepositResult] = useState(null);
    const [kycStatus, setKycStatus] = useState(null);

    const fileRef = useRef();

    useEffect(() => {
        if (auth.isAuthenticated && auth.token) {
            fetchPaymentMethods();
            fetchKYCStatus();
        } else {
            navigate(process.env.PUBLIC_URL + '/sign-in', { replace: true });
        }
    }, [auth, navigate]);

    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getPaymentMethods();

            if (response.success) {
                const methods = response.data?.paymentMethods || response.data || [];
                const activeMethod = methods.find(m => m.status === 'active');

                setPaymentMethods(Array.isArray(methods) ? methods : []);
                setLimits(response.data?.limits);
                setFees(response.data?.fees);
                setSystemStatus(response.data?.systemStatus);
                setVerificationStatus(response.data?.verificationStatus);

                if (activeMethod) {
                    setFormData(prev => ({
                        ...prev,
                        paymentMethodId: activeMethod._id
                    }));
                    setSelectedMethod(activeMethod);
                }
            } else {
                throw new Error(response.message || 'Failed to fetch payment methods');
            }
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            setError(error.message || 'Failed to fetch payment methods');
            setPaymentMethods([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchKYCStatus = async () => {
        try {
            const response = await getKYCDetails();
            if (response.success && response.data?.kyc?.status) {
                setKycStatus(response.data.kyc.status);
            } else {
                setKycStatus('not_found');
            }
        } catch (error) {
            console.error('Error fetching KYC status:', error);
            setKycStatus('not_found');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'paymentMethodId') {
            const method = paymentMethods.find(m => m._id === value);
            setSelectedMethod(method);
        }
        setError(null);
        setSuccess(null);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                receipt: file
            }));
        }
    };

    const TRANSACTION_MEDIUMS = [
        { value: 'UPI', label: 'UPI' },
        { value: 'NEFT', label: 'NEFT' },
        { value: 'RTGS', label: 'RTGS' },
        { value: 'IMPS', label: 'IMPS' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'credit', label: 'Credit' },
        { value: 'crypto', label: 'Crypto' },
        { value: 'bonus', label: 'Bonus' },
        { value: 'referral_bonus', label: 'Referral Bonus' },
        { value: 'demat_transfer', label: 'Demat Transfer' },
    ];

    const validateForm = () => {
        if (!formData.amount || isNaN(formData.amount) || formData.amount <= 0) {
            setError('Please enter a valid amount');
            return false;
        }

        if (!formData.paymentMethodId) {
            setError('Please select a payment method');
            return false;
        }

        if (!formData.transactionId) {
            setError('Please enter the bank transaction ID');
            return false;
        }

        if (!formData.medium) {
            setError('Please select a transaction medium');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);

            const response = await createDeposit(formData);
            console.log('Deposit response:', response);

            if (response.success) {
                const depositData = response.data;

                // Store the result with safe values
                setDepositResult({
                    id: depositData.depositId,
                    reference: depositData.reference,
                    amount: Number(depositData.amount),
                    status: depositData.status,
                    medium: depositData.medium,
                    createdAt: depositData.createdAt
                });

                setSuccess(response.message || 'Deposit submitted successfully!');

                // Reset form
                setFormData({
                    amount: '',
                    paymentMethodId: selectedMethod?._id || '',
                    transactionId: '',
                    medium: 'bank_transfer'
                });

                // Refresh payment methods to get updated limits
                fetchPaymentMethods();

                // Clear success message after 5 seconds
                setTimeout(() => {
                    setSuccess(null);
                    setDepositResult(null);
                }, 10000);

            } else {
                throw new Error(response.message || 'Failed to submit deposit request');
            }
        } catch (error) {
            console.error('Error creating deposit:', error);
            setError(error.message || error.data?.message || 'Failed to submit deposit request');
        } finally {
            setSubmitting(false);
        }
    };

    const calculateFees = (amount) => {
        if (!fees || !amount) return 0;
        const percentageFee = (amount * fees.percentage) / 100;
        const totalFee = percentageFee + fees.fixed;
        return Math.min(Math.max(totalFee, fees.min), fees.max);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!systemStatus?.depositsEnabled) {
        return (
            <div className="col-xl-12">
                <Card>
                    <Card.Body>
                        <Alert variant="warning">
                            Deposits are currently disabled. Please try again later.
                        </Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (systemStatus?.maintenanceMode) {
        return (
            <div className="col-xl-12">
                <Card>
                    <Card.Body>
                        <Alert variant="info">
                            System is under maintenance. Please try again later.
                        </Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="col-xl-12">
            <Card>
                <Card.Header className="py-3 d-flex justify-content-between bg-transparent border-bottom align-items-center">
                    <h6 className="mb-0 fw-bold">Deposit Funds</h6>
                </Card.Header>
                <Card.Body>
                    {(kycStatus && kycStatus !== 'approved') && (
                        <Alert variant="warning" className="mb-4">
                            Please complete your KYC verification to enable all deposit features.
                        </Alert>
                    )}

                    {success && (
                        <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess(null)}>
                            <strong>Success!</strong> {success}
                            {depositResult && (
                                <div className="mt-3">
                                    <table className="table table-sm table-bordered">
                                        <tbody>
                                            <tr>
                                                <td><strong>Deposit ID:</strong></td>
                                                <td>{depositResult.id}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Reference:</strong></td>
                                                <td>{depositResult.reference}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Amount:</strong></td>
                                                <td>${depositResult.amount.toLocaleString('en-IN', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Medium:</strong></td>
                                                <td>{depositResult.medium}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Status:</strong></td>
                                                <td>
                                                    <Badge bg={
                                                        depositResult.status === 'pending' ? 'warning' :
                                                        depositResult.status === 'approved' ? 'success' :
                                                        depositResult.status === 'rejected' ? 'danger' : 'secondary'
                                                    }>
                                                        {depositResult.status.toUpperCase()}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Alert>
                    )}

                    {error && (
                        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
                            <strong>Error!</strong> {error}
                        </Alert>
                    )}

                    <div className="row g-3">
                        <div className="col-xxl-4 col-xl-4 col-lg-5">
                            <div className="sticky-top" style={{ top: '20px', zIndex: 1 }}>
                                {selectedMethod && (
                                    <Card className="mb-3">
                                        <Card.Header>
                                            <Card.Title className="mb-0">Payment Details</Card.Title>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="payment-method-details">
                                                {selectedMethod.paymentTypes?.upi && (
                                                    <div className="upi-details mb-4">
                                                        <h5>QR Code</h5>
                                                        {selectedMethod.upiQrCode && (
                                                            <div className="text-center mb-3">
                                                                <img
                                                                    src={buildImageUrl(selectedMethod.upiQrCode)}
                                                                    alt="QR Code"
                                                                    style={{
                                                                        maxWidth: '200px',
                                                                        width: '100%',
                                                                        borderRadius: '8px'
                                                                    }}
                                                                    onError={(e) => {
                                                                        console.error('Failed to load QR code image:', e.target.src);
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                        {selectedMethod.upiId && (
                                                            <div className="upi-id-container">
                                                                <p className="mb-2">Address:</p>
                                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                    <code className="fs-5 text-break" style={{ wordBreak: 'break-all' }}>{selectedMethod.upiId}</code>
                                                                    <Button
                                                                        variant="light"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(selectedMethod.upiId);
                                                                            setSuccess('Address copied to clipboard');
                                                                            setTimeout(() => setSuccess(null), 2000);
                                                                        }}
                                                                    >
                                                                        Copy
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {selectedMethod.paymentTypes?.bankTransfer && (
                                                    <div className="bank-details">
                                                        <h5>Bank Details</h5>
                                                        <table className="table table-sm">
                                                            <tbody>
                                                                <tr>
                                                                    <td>Account Name:</td>
                                                                    <td>{selectedMethod.accountHolderName}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Bank Name:</td>
                                                                    <td>{selectedMethod.bankName}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Account No:</td>
                                                                    <td>
                                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                            <code className="text-break" style={{ wordBreak: 'break-all' }}>{selectedMethod.accountNumber}</code>
                                                                            <Button
                                                                                variant="light"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    navigator.clipboard.writeText(selectedMethod.accountNumber);
                                                                                    setSuccess('Account number copied to clipboard');
                                                                                    setTimeout(() => setSuccess(null), 2000);
                                                                                }}
                                                                            >
                                                                                Copy
                                                                            </Button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td>IFSC Code:</td>
                                                                    <td>
                                                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                            <code className="text-break" style={{ wordBreak: 'break-all' }}>{selectedMethod.ifscCode}</code>
                                                                            <Button
                                                                                variant="light"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    navigator.clipboard.writeText(selectedMethod.ifscCode);
                                                                                    setSuccess('IFSC code copied to clipboard');
                                                                                    setTimeout(() => setSuccess(null), 2000);
                                                                                }}
                                                                            >
                                                                                Copy
                                                                            </Button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>

                                            {limits && (
                                                <Card className="mt-3">
                                                    <Card.Header>
                                                        <Card.Title className="mb-0">Deposit Limits</Card.Title>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="limits-info">
                                                            <div className="limit-item mb-3 d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <small className="d-block text-muted">Min Deposit</small>
                                                                    <h6 className="mb-0">${limits.minDeposit}</h6>
                                                                </div>
                                                                <div className="text-end">
                                                                    <small className="d-block text-muted">Max Deposit</small>
                                                                    <h6 className="mb-0">${limits.maxDeposit}</h6>
                                                                </div>
                                                            </div>

                                                            {(limits.dailyRemaining || limits.monthlyRemaining) && (
                                                                <div className="remaining-limits mt-3 pt-3 border-top">
                                                                    {limits.dailyRemaining && (
                                                                        <div className="mb-2">
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <small className="text-muted">Daily Remaining</small>
                                                                                <h6 className="mb-0">${limits.dailyRemaining}</h6>
                                                                            </div>
                                                                            <div className="progress mt-2" style={{ height: '6px' }}>
                                                                                <div
                                                                                    className="progress-bar bg-success"
                                                                                    style={{
                                                                                        width: `${(limits.dailyRemaining / limits.maxDeposit) * 100}%`
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {limits.monthlyRemaining && (
                                                                        <div>
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <small className="text-muted">Monthly Remaining</small>
                                                                                <h6 className="mb-0">${limits.monthlyRemaining}</h6>
                                                                            </div>
                                                                            <div className="progress mt-2" style={{ height: '6px' }}>
                                                                                <div
                                                                                    className="progress-bar bg-info"
                                                                                    style={{
                                                                                        width: `${(limits.monthlyRemaining / limits.maxDeposit) * 100}%`
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            )}
                                        </Card.Body>
                                    </Card>
                                )}
                            </div>
                        </div>

                        <div className="col-xxl-8 col-xl-8 col-lg-7">
                            {!selectedMethod ? (
                                <Alert variant="warning">
                                    No payment methods available at the moment.
                                </Alert>
                            ) : (
                                <Form onSubmit={handleSubmit} className="deposit-form">
                                    <Form.Group className="mb-4">
                                        <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
                                        <div className="amount-input-wrapper">
                                            <Form.Control
                                                type="number"
                                                name="amount"
                                                value={formData.amount}
                                                onChange={handleInputChange}
                                                min={limits?.minDeposit || 1}
                                                max={limits?.maxDeposit}
                                                required
                                                className="form-control-lg"
                                                placeholder="Enter amount"
                                            />
                                            {fees && formData.amount && (
                                                <div className="fee-info mt-2 p-3 bg-light rounded">
                                                    <div className="d-flex justify-content-between">
                                                        <span className="text-muted">Amount:</span>
                                                        <span>${formData.amount}</span>
                                                    </div>
                                                    {fees.percentage > 0 && (
                                                        <div className="d-flex justify-content-between mt-1">
                                                            <span className="text-muted">Fee ({fees.percentage}%):</span>
                                                            <span>${calculateFees(formData.amount).toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    {fees.fixed > 0 && (
                                                        <div className="d-flex justify-content-between mt-1">
                                                            <span className="text-muted">Fixed Fee:</span>
                                                            <span>${fees.fixed}</span>
                                                        </div>
                                                    )}
                                                    <div className="d-flex justify-content-between mt-1 pt-2 border-top">
                                                        <span className="text-muted"><strong>Total:</strong></span>
                                                        <span className="font-w600">
                                                            ${(Number(formData.amount) + calculateFees(formData.amount)).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Transaction Medium <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            name="medium"
                                            value={formData.medium}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control-lg"
                                        >
                                            {TRANSACTION_MEDIUMS.map(medium => (
                                                <option key={medium.value} value={medium.value}>
                                                    {medium.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Select the method used for bank transfer
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Transaction ID <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="transactionId"
                                            value={formData.transactionId}
                                            onChange={handleInputChange}
                                            required
                                            placeholder={`Enter ${formData.medium} transaction ID`}
                                            className="form-control-lg"
                                        />
                                        <Form.Text className="text-muted">
                                            Enter the transaction ID from your {formData.medium} transfer
                                        </Form.Text>
                                    </Form.Group>

                                    <div className="text-center mt-4">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            size="lg"
                                            disabled={submitting ||
                                                !selectedMethod ||
                                                limits?.limitsExceeded?.daily ||
                                                limits?.limitsExceeded?.monthly}
                                        >
                                            {submitting ? 'Processing...' : 'Submit Deposit'}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(DepositNew);
